// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import uuidv4 from "uuid/v4";
import equal from "fast-deep-equal";
import React, { ChangeEvent, MouseEvent, useState } from "react";

import { SupervisorDocument, Supervisor, Uid } from "../store/schema";
import { getApi } from "../store/api";
import { logAndTrackA, UID_NEW, throwIfNull } from "../util";
import { useAsyncGuard } from "../UiUtil";
import {
  Concern,
  validate,
  ONE_OR_MORE_NAME_VALIDATORS,
  ValidatedTextInputDetail,
  EMAIL_VALIDATORS,
  PHONE_NUMBER_VALIDATORS,
  isValidationError,
  UID_VALIDATORS,
  ValidatedCreateButton,
  ValidatedPhoneInputDetail,
} from "./ValidatedUI";

const CHOOSE_DOC = { data: { uid: "", name: "<Choose a supervisor>" } };
const NEW_DOC = { data: { uid: UID_NEW, name: "---< Create New >---" } };

export function isFakeSupervisorUid(uid: string) {
  return uid === CHOOSE_DOC.data.uid || uid === NEW_DOC.data.uid;
}

export type SupervisorFields = {
  name: string;
  email: string;
  phone: string;
};

export type SupervisorCreator = (
  uid: Uid,
  fields: SupervisorFields
) => Supervisor;

interface SupervisorChooserDetailsProps {
  disabled: boolean;
  supervisors: SupervisorDocument[];
  organizationUid: string;
  supervisorUid: string;
  onCreateSupervisor?: SupervisorCreator;
  onChangeSupervisor: (uid: string) => void;
  className: string;
  errorText?: string;
}

export const SupervisorChooserDetails: React.FC<SupervisorChooserDetailsProps> = ({
  supervisors,
  organizationUid,
  supervisorUid,
  onCreateSupervisor,
  onChangeSupervisor,
  className,
  errorText,
}) => {
  const [saving, setSaving] = useState(false);
  const [superName, setSuperName] = useState("");
  const [superEmail, setSuperEmail] = useState("");
  const [superPhone, setSuperPhone] = useState("");

  const api = getApi();
  const guard = useAsyncGuard();

  function parseForm(): SupervisorFields {
    return {
      name: superName,
      email: superEmail,
      phone: superPhone,
    };
  }

  function fields(supervisor: Supervisor): SupervisorFields {
    const { name, email, phone } = supervisor;
    return { name, email, phone };
  }

  function onCreate(e: MouseEvent<HTMLButtonElement>) {
    const create = throwIfNull(onCreateSupervisor);
    const newUid = uuidv4();
    logAndTrackA(setSaving, async () => {
      onChangeSupervisor(newUid);
      await guard(api.volunteer.saveSupervisor(create(newUid, parseForm())));
    });
  }

  const concerns: { [key: string]: Concern | null } = {
    name: validate(ONE_OR_MORE_NAME_VALIDATORS, superName),
    email: validate(EMAIL_VALIDATORS, superEmail),
    phone: validate(PHONE_NUMBER_VALIDATORS, superPhone),
  };

  const anyErrors = Object.keys(concerns)
    .map(key => concerns[key])
    .some(isValidationError);

  function canCreate(): boolean {
    return (
      !saving &&
      !anyErrors &&
      !supervisors.some(x => equal(parseForm(), fields(x.data)))
    );
  }

  return (
    <>
      <div className={className}>
        <label htmlFor="supervisorUid">Supervisor:</label>
        <SupervisorChooser
          id="supervisorUid"
          disabled={saving}
          supervisors={supervisors}
          organizationUid={organizationUid}
          supervisorUid={supervisorUid}
          onChangeSupervisor={onChangeSupervisor}
        />
        {errorText && <div className="error-message">{errorText}</div>}
      </div>
      {supervisorUid === UID_NEW && (
        <>
          <ValidatedTextInputDetail
            htmlId="supervisor-name"
            label="Supervisor Name:"
            value={superName}
            onChange={setSuperName}
            concern={concerns.name}
          />
          <ValidatedTextInputDetail
            htmlId="supervisor-email"
            label="Supervisor Email:"
            type="email"
            inputMode="email"
            value={superEmail}
            onChange={setSuperEmail}
            concern={concerns.email}
          />
          <ValidatedPhoneInputDetail
            htmlId="supervisor-phone"
            label="Supervisor Phone:"
            value={superPhone}
            onChange={setSuperPhone}
            concern={concerns.phone}
          />
          {!anyErrors && (
            <ValidatedCreateButton
              disabled={!canCreate()}
              buttonText={saving ? "Creating" : "Create"}
              onClick={onCreate}
              concern={validate(UID_VALIDATORS, supervisorUid)}
            />
          )}
        </>
      )}
    </>
  );
};

interface SupervisorChooserProps {
  id?: string;
  supervisors: SupervisorDocument[];
  organizationUid: string;
  supervisorUid: string;
  onChangeSupervisor: (uid: string) => void;
  disabled: boolean;
}

export const SupervisorChooser: React.FC<SupervisorChooserProps> = ({
  id,
  supervisors,
  organizationUid,
  supervisorUid,
  onChangeSupervisor,
  disabled,
}) => {
  debug("args", { id, organizationUid, supervisorUid, disabled });
  function onChange(e: ChangeEvent<HTMLSelectElement>) {
    const uid = e.target.value;
    onChangeSupervisor(uid);
  }

  function sorted(orgs: SupervisorDocument[]): SupervisorDocument[] {
    return [...orgs].sort(compare);
  }

  function compare(a: SupervisorDocument, b: SupervisorDocument): number {
    // Supervisors from same org come first
    const aOrg = a.data.organizationUid === organizationUid ? 0 : 1;
    const bOrg = b.data.organizationUid === organizationUid ? 0 : 1;
    if (aOrg < bOrg) return -1;
    if (aOrg > bOrg) return 1;

    const aName = a.data.name;
    const bName = b.data.name;
    if (aName < bName) return -1;
    if (aName > bName) return 1;

    return 0;
  }

  const choices = [
    CHOOSE_DOC,
    NEW_DOC,
    ...sorted(supervisors.filter(x => !x.data.isDeleted)),
  ];

  return (
    <div>
      <div>
        <select
          id={id}
          disabled={disabled}
          onChange={onChange}
          value={supervisorUid}
        >
          {choices.map(x => (
            <option key={x.data.uid} value={x.data.uid}>
              {x.data.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

function debug(message: string, state?: any) {
  if (false) {
    console.log(`SupervisorChooser: ${message}`);
    if (state != null) {
      console.log(state);
    }
  }
}
